import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const slides = [
  {
    id: 1,
    bg: "/assets/hero.png",
    overlay: "bg-gradient-to-r from-[#003366]/85 via-[#003366]/60 to-transparent",
    align: "items-center justify-start",
    textAlign: "text-left",
    tag: "Líderes en Telecomunicaciones",
    title: "Soluciones en Telecomunicaciones",
    subtitle: "Infraestructura de red de alta performance para tu hogar y empresa.",
    cta1: { label: "Ver Servicios", href: "#servicios" },
    cta2: { label: "Conocé más", href: "#contacto" },
    titleColor: "text-white",
  },
  {
    id: 2,
    bg: "https://msvision.com.ar/wp-content/uploads/2023/11/WhatsApp-Image-2020-01-06-at-4.25.13-PM.jpeg",
    overlay: "bg-[#003366]/75",
    align: "items-center justify-center",
    textAlign: "text-center",
    tag: "Más de 10 años de experiencia",
    title: "Ms Visión Telecomunicaciones",
    subtitle: "Conectando el futuro. Redes de alta velocidad para hogares y empresas de todo el AMBA.",
    cta1: { label: "Nuestros Servicios", href: "#servicios" },
    cta2: null,
    titleColor: "text-white",
  },
  {
    id: 3,
    bg: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80",
    overlay: "bg-gradient-to-r from-[#1A1A2E]/90 via-[#003366]/70 to-transparent",
    align: "items-center justify-start",
    textAlign: "text-left",
    tag: "Tecnología de Vanguardia",
    title: "HFC · GPON · Fibra Óptica",
    subtitle: "Diseñamos, instalamos y certificamos infraestructura de red de última generación.",
    cta1: { label: "Ver Proyectos", href: "#proyectos" },
    cta2: { label: "Contactanos", href: "#contacto" },
    titleColor: "text-white",
  },
];

const textVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const goTo = useCallback(
    (idx) => {
      setDirection(idx > current ? 1 : -1);
      setCurrent(idx);
    },
    [current]
  );

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slideVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 80 : -80 }),
    center: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeInOut" } },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -80 : 80, transition: { duration: 0.5 } }),
  };

  return (
    <section id="inicio" data-testid="hero-section" className="relative h-screen overflow-hidden">
      {/* BG Slides */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={current}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${slides[current].bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </AnimatePresence>

      {/* Overlay */}
      <div className={`absolute inset-0 ${slides[current].overlay} transition-all duration-700`} />

      {/* Content */}
      <div className={`relative z-10 h-full flex ${slides[current].align} px-8 md:px-16 lg:px-24`}>
        <AnimatePresence mode="wait">
          <div
            key={`content-${current}`}
            className={`max-w-2xl ${slides[current].textAlign}`}
          >
            <motion.span
              custom={0}
              initial="hidden"
              animate="visible"
              variants={textVariants}
              className="inline-block text-accent font-heading font-semibold text-sm tracking-widest uppercase bg-accent/10 border border-accent/30 px-4 py-1.5 rounded-full mb-5"
            >
              {slides[current].tag}
            </motion.span>

            <motion.h1
              custom={1}
              initial="hidden"
              animate="visible"
              variants={textVariants}
              className={`font-heading font-black text-4xl md:text-6xl lg:text-7xl leading-tight mb-5 ${slides[current].titleColor}`}
            >
              {slides[current].title}
            </motion.h1>

            <motion.p
              custom={2}
              initial="hidden"
              animate="visible"
              variants={textVariants}
              className="font-body text-white/85 text-lg md:text-xl mb-8 leading-relaxed max-w-lg"
            >
              {slides[current].subtitle}
            </motion.p>

            <motion.div
              custom={3}
              initial="hidden"
              animate="visible"
              variants={textVariants}
              className={`flex flex-wrap gap-4 ${slides[current].textAlign === "text-center" ? "justify-center" : ""}`}
            >
              <a
                href={slides[current].cta1.href}
                data-testid={`hero-cta-primary-${current}`}
                className="bg-accent text-white px-8 py-4 rounded-full font-heading font-bold flex items-center gap-2 hover:bg-[#0099D6] hover:shadow-[0_0_30px_rgba(0,174,239,0.6)] transition-all duration-300 transform hover:scale-105"
              >
                {slides[current].cta1.label}
                <ArrowRight size={18} />
              </a>
              {slides[current].cta2 && (
                <a
                  href={slides[current].cta2.href}
                  data-testid={`hero-cta-secondary-${current}`}
                  className="bg-transparent border-2 border-white/70 text-white px-8 py-4 rounded-full font-heading font-bold hover:bg-white hover:text-primary transition-all duration-300"
                >
                  {slides[current].cta2.label}
                </a>
              )}
            </motion.div>
          </div>
        </AnimatePresence>
      </div>

      {/* Arrows */}
      <button
        data-testid="hero-prev"
        onClick={prev}
        className="absolute left-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-accent hover:border-accent transition-all duration-300"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        data-testid="hero-next"
        onClick={next}
        className="absolute right-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-accent hover:border-accent transition-all duration-300"
      >
        <ChevronRight size={22} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-400 ${
              i === current
                ? "w-8 h-3 bg-accent"
                : "w-3 h-3 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 right-8 z-20 hidden md:flex flex-col items-center gap-2">
        <span className="text-white/50 text-xs font-body tracking-widest uppercase rotate-90 origin-center mb-4">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent animate-bounce-subtle" />
      </div>
    </section>
  );
};
